import {
	Paper,
	styled,
	Table,
	TableBody,
	TableContainer,
	TablePagination,
} from "@mui/material";
import { types } from "api";
import { getComparator, Order, stableSort } from "helpers/tableComparator";
import React, { useState } from "react";
import ReactLoading from "react-loading";
import { GroupTableHead } from "./GroupTableHead";
import { GroupTableRow } from "./GroupTableRow";
import { GroupTableToolbar } from "./GroupTableToolbar";

const PREFIX = "GroupTable";

const classes = {
	root: `${PREFIX}-root`,
	paper: `${PREFIX}-paper`,
	table: `${PREFIX}-table`,
	hide: `${PREFIX}-hide`,
	button: `${PREFIX}-button`,
	errBtn: `${PREFIX}-errBtn`,
	load: `${PREFIX}-load`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		width: "100%",
	},
	[`& .${classes.paper}`]: {
		width: "100%",
		marginBottom: theme.spacing(2),
	},

	[`& .${classes.table}`]: {
		minWidth: 750,
	},

	[`& .${classes.hide}`]: {
		display: "none",
	},

	[`& .${classes.button}`]: {
		float: "left",
		margin: "10",
	},

	[`& .${classes.errBtn}`]: {
		color: theme.palette.error.main,
	},

	[`& .${classes.load}`]: {
		marginLeft: theme.spacing(20),
	},
}));

export interface GroupTableProps {
	/**
	 * Additional toolbar buttons.
	 */
	children?: JSX.Element | JSX.Element[];
	groups: types.Group[];
	title?: string;
	isloading: boolean;
}

export const GroupTable = ({
	children,
	groups,
	title = "Group List",
	isloading,
}: GroupTableProps): JSX.Element => {
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof types.Group>("name");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof types.Group,
	): void => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleChangePage = (event: unknown, newPage: number): void => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<Root className={classes.root}>
			<Paper className={classes.paper}>
				<GroupTableToolbar title={title}>{children}</GroupTableToolbar>

				<TableContainer>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						aria-label="enhanced table"
					>
						<GroupTableHead
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
						/>
						<TableBody>
							{isloading ? (
								<ReactLoading
									className={classes.load}
									type={"bars"}
									color={"#09203F"}
								/>
							) : (
								stableSort(groups, getComparator(order, orderBy))
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((row) => <GroupTableRow row={row} key={row.groupId} />)
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={groups.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>
		</Root>
	);
};
